import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  Zap, 
  Code, 
  Link2,
  Workflow,
  Clock,
  Target,
  Home,
  Settings,
  Database
} from 'lucide-react';

const Prozessautomatisierung = () => {
  return (
    <div className="page-container">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Prozessautomatisierung</span>
          </div>
        </div>
      </div>

      <section className="hero-section-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Banner Image */}
          <div className="mb-8 rounded-xl overflow-hidden" style={{maxHeight: '200px'}}>
            <img 
              src="https://images.pexels.com/photos/373543/pexels-photo-373543.jpeg" 
              alt="Prozessautomatisierung" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h1 className="service-page-title">
                Prozessautomatisierung – Workflows digitalisieren, Zeit sparen
              </h1>
              <p className="service-page-subtitle">
                Von manuellen Prozessen zu automatisierten Workflows: Ich programmiere APIs, erstelle Schnittstellen und automatisiere Ihre wiederkehrenden Aufgaben. Sparen Sie bis zu 90% Zeit durch intelligente Automatisierung.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/#kontakt">
                  <Button size="lg" className="cta-button-large">
                    Kostenlose Erstanalyse
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>30-90% weniger Aufwand</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Keine Fehler mehr</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>24/7 Automation</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">Was ich automatisiere</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Link2 className="h-6 w-6" />
                </div>
                <h3 className="service-title">API-Integration</h3>
                <p className="service-description">
                  Verbindung zwischen Systemen, Datenabgleich, REST/SOAP APIs und Echtzeit-Synchronisation.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Workflow className="h-6 w-6" />
                </div>
                <h3 className="service-title">Workflow-Automation</h3>
                <p className="service-description">
                  Automatische Auftragsabwicklung, Rechnungserstellung, E-Mail-Versand und Statusupdates.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Code className="h-6 w-6" />
                </div>
                <h3 className="service-title">Custom Scripts</h3>
                <p className="service-description">
                  Python-Programmierung, Datenverarbeitung, individuelle Lösungen für Ihre spezifischen Anforderungen.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Database className="h-6 w-6" />
                </div>
                <h3 className="service-title">Daten-Synchronisation</h3>
                <p className="service-description">
                  Automatischer Abgleich zwischen Warenwirtschaft, Shop, Marktplätzen und Buchhaltung.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Settings className="h-6 w-6" />
                </div>
                <h3 className="service-title">System-Integration</h3>
                <p className="service-description">
                  JTL, Shopware, Amazon, eBay – alle Systeme sprechen miteinander ohne manuelle Eingriffe.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Zap className="h-6 w-6" />
                </div>
                <h3 className="service-title">KI-Automatisierung</h3>
                <p className="service-description">
                  Intelligente Datenverarbeitung, automatische Kategorisierung und predictive Analytics.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="visual-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-dark">Typische Zeitersparnis</h2>
          <div className="text-6xl font-bold text-accent my-8">30-90%</div>
          <p className="text-xl text-gray-300">
            Unternehmen reduzieren ihren manuellen Aufwand um durchschnittlich 30-90% durch intelligente Prozessautomatisierung.
          </p>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Bereit für automatisierte Prozesse?</h2>
          <p className="section-subtitle mb-8">
            Lassen Sie uns analysieren, welche Ihrer Prozesse automatisiert werden können.
          </p>
          <Link to="/#kontakt">
            <Button size="lg" className="cta-button-large">
              Jetzt kostenlose Erstanalyse anfragen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Prozessautomatisierung;